import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { getIds, useTabContext } from './TabControllerContext';

/**
 * The content to be displayed for each tab.
 *
 * Related components: [Tabs](#tabs), [Tab](#tab), [TabList](#tablist), [TabController](#tabcontroller)
 *
 * Usage:
 *
 * ```jsx
 * import { TabPanel } from '@one-thd/sui-atomic-components';
 * ```
 */
const TabPanel = React.forwardRef((props, ref) => {

  const {
    children,
    classes,
    className,
    value,
    ...other
  } = props;

  const context = useTabContext();
  if (context === null) {
    throw new TypeError('No TabController provided');
  }

  const { tabId, panelId } = getIds(context, value) || {};

  return (
    <div
      aria-labelledby={tabId}
      className={classNames(classes?.root, className)}
      hidden={value !== context.value}
      id={panelId}
      ref={ref}
      role="tabpanel"
      {...other}
    >
      {value === context.value && children}
    </div>
  );
});

TabPanel.displayName = 'TabPanel';

TabPanel.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * Value assigned to the panel component.
   */
  value: PropTypes.string
};

export { TabPanel };
